import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonModal,
  IonSearchbar,
  IonSpinner,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import {
  businessOutline,
  checkmarkCircle,
  chevronForward,
  closeOutline,
  locationOutline,
  searchOutline,
} from 'ionicons/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  businessDirectoryZones,
  filterBusinessDirectory,
  normalizeDirectoryText,
  type BusinessDirectoryEntry,
} from '../lib/auth/businessDirectory.js';
import './BusinessDirectoryModal.css';

const MAX_VISIBLE_RESULTS = 100;

function sameName(left: string, right: string): boolean {
  return normalizeDirectoryText(left) === normalizeDirectoryText(right);
}

function locationLabel(entry: BusinessDirectoryEntry): string {
  const city = entry.city
    ? `${entry.city}${entry.provincia ? ` (${entry.provincia})` : ''}`
    : 'Zona non indicata';
  return entry.address ? `${entry.address} · ${city}` : city;
}

function accessibleEntryLabel(entry: BusinessDirectoryEntry): string {
  const business = sameName(entry.businessName, entry.tenantName) ? '' : `, ${entry.businessName}`;
  return `${entry.tenantName}${business}, ${locationLabel(entry)}`;
}

export interface BusinessDirectoryModalProps {
  isOpen: boolean;
  entries: BusinessDirectoryEntry[];
  activeTenantId: string | null;
  activeBusinessId: string | null;
  loading?: boolean;
  loadError?: boolean;
  onRetry?: () => void;
  onClose: () => void;
  onSelect: (entry: BusinessDirectoryEntry) => Promise<void>;
}

export function BusinessDirectoryModal(props: BusinessDirectoryModalProps): JSX.Element {
  const {
    isOpen,
    entries,
    activeTenantId,
    activeBusinessId,
    loading,
    loadError,
    onRetry,
    onClose,
    onSelect,
  } = props;
  const [query, setQuery] = useState('');
  const [zoneKey, setZoneKey] = useState<string | null>(null);
  const [switchingId, setSwitchingId] = useState<string | null>(null);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const searchRef = useRef<HTMLIonSearchbarElement>(null);
  const zones = useMemo(() => businessDirectoryZones(entries), [entries]);
  const results = useMemo(() => {
    const filtered = filterBusinessDirectory(entries, query, zoneKey);
    return filtered.sort((left, right) => {
      const leftActive = left.tenantId === activeTenantId
        && (left.businessId === null || left.businessId === activeBusinessId);
      const rightActive = right.tenantId === activeTenantId
        && (right.businessId === null || right.businessId === activeBusinessId);
      return Number(rightActive) - Number(leftActive);
    });
  }, [activeBusinessId, activeTenantId, entries, query, zoneKey]);

  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setZoneKey(null);
    setSwitchError(null);
    setSwitchingId(null);
  }, [isOpen]);

  useEffect(() => {
    if (zoneKey && !zones.some((zone) => zone.key === zoneKey)) setZoneKey(null);
  }, [zoneKey, zones]);

  async function selectEntry(entry: BusinessDirectoryEntry): Promise<void> {
    const isActive = entry.tenantId === activeTenantId
      && (entry.businessId === null || entry.businessId === activeBusinessId);
    if (isActive) {
      onClose();
      return;
    }
    setSwitchingId(entry.id);
    setSwitchError(null);
    try {
      await onSelect(entry);
      onClose();
    } catch {
      setSwitchError('Non è stato possibile cambiare locale. Controlla la connessione e riprova.');
    } finally {
      setSwitchingId(null);
    }
  }

  const visibleResults = results.slice(0, MAX_VISIBLE_RESULTS);

  return (
    <IonModal
      className="te-business-directory-modal"
      isOpen={isOpen}
      onDidDismiss={onClose}
      onDidPresent={() => {
        requestAnimationFrame(() => requestAnimationFrame(() => { void searchRef.current?.setFocus(); }));
      }}
    >
      <IonHeader>
        <IonToolbar>
          <IonTitle>Scegli il locale</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onClose} aria-label="Chiudi la scelta del locale">
              <IonIcon icon={closeOutline} slot="icon-only" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="te-business-directory-tools">
          <IonSearchbar
            ref={searchRef}
            className="te-business-directory-search"
            value={query}
            debounce={80}
            inputmode="search"
            enterkeyhint="search"
            placeholder="Cerca locale, città o indirizzo"
            aria-label="Cerca tra i locali"
            onIonInput={(event) => setQuery(String(event.detail.value ?? ''))}
          />
          <div className="te-business-directory-zones" aria-label="Filtra i locali per zona">
            <button
              type="button"
              className="te-business-directory-zone"
              aria-pressed={zoneKey === null}
              onClick={() => setZoneKey(null)}
            >
              Tutte · {entries.length}
            </button>
            {zones.map((zone) => (
              <button
                key={zone.key}
                type="button"
                className="te-business-directory-zone"
                aria-pressed={zoneKey === zone.key}
                onClick={() => setZoneKey(zone.key)}
              >
                {zone.label} · {zone.count}
              </button>
            ))}
          </div>
        </div>

        <div className="te-business-directory-list">
          <div className="te-business-directory-summary" aria-live="polite">
            {loading ? 'Aggiornamento locali…' : `${results.length} ${results.length === 1 ? 'locale trovato' : 'locali trovati'}`}
          </div>

          {loadError && (
            <div className="te-business-directory-message te-business-directory-message--error" role="alert">
              L’elenco aggiornato non è disponibile. Puoi usare i risultati già visibili oppure riprovare.
              {onRetry && (
                <div>
                  <button type="button" className="te-business-directory-retry" onClick={onRetry}>
                    Riprova
                  </button>
                </div>
              )}
            </div>
          )}

          {switchError && (
            <div className="te-business-directory-message te-business-directory-message--error" role="alert">
              {switchError}
            </div>
          )}

          {entries.length === 0 && loading ? (
            <div className="te-business-directory-empty">
              <IonSpinner name="crescent" aria-label="Caricamento locali" />
              <div>Sto caricando le attività disponibili…</div>
            </div>
          ) : results.length === 0 ? (
            <div className="te-business-directory-empty">
              <IonIcon icon={searchOutline} style={{ fontSize: 30 }} aria-hidden="true" />
              <div>Nessun locale corrisponde alla ricerca e alla zona selezionata.</div>
            </div>
          ) : (
            visibleResults.map((entry) => {
              const isActive = entry.tenantId === activeTenantId
                && (entry.businessId === null || entry.businessId === activeBusinessId);
              const isSwitching = switchingId === entry.id;
              return (
                <button
                  key={entry.id}
                  type="button"
                  className="te-business-directory-item"
                  aria-current={isActive ? 'true' : undefined}
                  aria-label={`${accessibleEntryLabel(entry)}${isActive ? ', locale attivo' : ''}`}
                  disabled={switchingId !== null}
                  onClick={() => { void selectEntry(entry); }}
                >
                  <span className="te-business-directory-icon" aria-hidden="true">
                    <IonIcon icon={businessOutline} />
                  </span>
                  <span>
                    <span className="te-business-directory-name">{entry.tenantName}</span>
                    {!sameName(entry.businessName, entry.tenantName) && (
                      <span className="te-business-directory-tenant">{entry.businessName}</span>
                    )}
                    <span className="te-business-directory-location">
                      <IonIcon icon={locationOutline} aria-hidden="true" />
                      <span>{locationLabel(entry)}</span>
                    </span>
                    {isActive && <span className="te-business-directory-current">Locale attivo</span>}
                  </span>
                  {isSwitching
                    ? <IonSpinner name="crescent" aria-label="Cambio locale in corso" />
                    : <IonIcon icon={isActive ? checkmarkCircle : chevronForward} aria-hidden="true" />}
                </button>
              );
            })
          )}

          {results.length > MAX_VISIBLE_RESULTS && (
            <div className="te-business-directory-message">
              Sono mostrati i primi {MAX_VISIBLE_RESULTS} risultati. Usa la ricerca o una zona per restringere l’elenco.
            </div>
          )}
        </div>
      </IonContent>
    </IonModal>
  );
}
